/**
 * The Word comments of a document, read from the comment parts of the file,
 * keyed by the reference id the paragraph markup carries.
 *
 * A comment lives in three places. The paragraph holds only markers
 * (`w:commentRangeStart`, `w:commentRangeEnd`, a run with
 * `w:commentReference w:id="76"`). `word/comments.xml` holds the body: a
 * `w:comment w:id="76" w:author="…" w:date="…"` with paragraphs of text.
 * `word/commentsIds.xml` maps a comment's paragraph `paraId` to the durable
 * id Word keeps stable across saves, which is the id the core's reply tool
 * takes. `word/commentsExtended.xml` says, by the same `paraId`, which
 * comments are resolved and which comment each reply answers
 * (`w15:commentEx w15:paraId w15:paraIdParent w15:done`). This class joins
 * them so the reader route can list, per paragraph, the comments anchored in
 * it, with when each was written, which comment it answers and whether its
 * thread is resolved, without the core parsing anything.
 *
 * The reader keeps the `w:id` reference as the fallback when a durable id is
 * missing, the same rule the core's own `map_comment_wids_to_durable_ids`
 * applied.
 */
import { flatOpcPartXml, parseXmlDocumentOrNull } from "./flat-opc-parts";
import {
    COMMENTS_EXTENDED_PART_NAME,
    COMMENTS_IDS_PART_NAME,
    COMMENTS_PART_NAME,
    WORDML_2010_NS,
    WORDML_2012_NS,
    WORDML_2016_CID_NS,
    WORDPROCESSINGML_NS,
    elementNameWithoutPrefix,
    wordIdOf,
} from "./ooxml-names";

export interface DocumentComment {
    /** The durable comment id as a decimal string, or the `w:id` reference when the file carries no durable id. */
    id: string;
    author: string;
    text: string;
    /**
     * When the comment was written: its `w:date` as Word stores it (ISO 8601).
     *
     * NULL
     *   The comment carries no date, which is what Word writes when the author
     *   chose to remove personal information from the file.
     */
    date: string | null;
    /**
     * The `id` of the comment this one replies to, in the same id space as `id`.
     *
     * NULL
     *   A comment that starts a thread, or a file without a commentsExtended part
     *   (written before Word 2013 threaded replies), where every comment reads as
     *   its own thread.
     */
    repliesTo: string | null;
    /** True when the comment's thread is resolved in Word: the comment itself, or the comment it replies to, is marked done. */
    resolved: boolean;
}

/** The three comment parts of a `.docx`, as strings, each null when the file lacks it. */
export interface CommentParts {
    commentsXml: string | null;
    commentsIdsXml: string | null;
    commentsExtendedXml: string | null;
}

/** One comment as the first pass reads it: the comment, plus the `paraId` of each of its paragraphs, which is how a reply names its parent. */
interface CommentWithParagraphIds {
    referenceId: string;
    comment: DocumentComment;
    paragraphIds: string[];
}

/** One `w15:commentEx` entry: whether Word marked that comment done, and the `paraId` of the comment it replies to. */
interface CommentExtension {
    markedDone: boolean;
    repliedToParaId: string | null;
}

/**
 * Durable ids are 32-bit hex in the file and Word rejects the top bit, so a
 * value at or above this is malformed and the reference id is kept instead.
 */
const DURABLE_ID_MAX_EXCLUSIVE = 0x7fffffff;
const DURABLE_ID_HEX = /^[0-9A-Fa-f]{1,8}$/;
/** The spellings OOXML's on/off type (`ST_OnOff`) gives "on"; Word writes `w15:done="1"`. */
const OOXML_TRUE_VALUES: ReadonlySet<string> = new Set(["1", "true", "on"]);

export class DocumentComments {
    private constructor(private readonly commentByReferenceId: ReadonlyMap<string, DocumentComment>) {}

    /** A document with no comments, or one whose comment parts were not available to the caller. */
    static none(): DocumentComments {
        return new DocumentComments(new Map());
    }

    /** Reads the comment parts of an unpacked `.docx`. */
    static fromParts(parts: CommentParts): DocumentComments {
        /**
         * WHEN the file has no comments part
         *   CONSEQUENCE
         *     there are no comments to list; a commentsIds part alone names nothing.
         */
        if (parts.commentsXml === null) return DocumentComments.none();
        const commentsDocument = parseXmlDocumentOrNull(parts.commentsXml);
        /**
         * CASES
         *   - the file has a commentsIds part -> parse it, so durable ids can be looked up
         *   - it has none (older Word)        -> null: every comment keeps its `w:id` reference as its id
         */
        const commentsIdsDocument = parts.commentsIdsXml === null ? null : parseXmlDocumentOrNull(parts.commentsIdsXml);
        /**
         * CASES
         *   - the file has a commentsExtended part -> parse it, so resolved threads and replies can be told apart
         *   - it has none, or it does not parse    -> null: every comment reads as open and as its own thread
         */
        const commentsExtendedDocument = parts.commentsExtendedXml === null ? null : parseXmlDocumentOrNull(parts.commentsExtendedXml);
        /**
         * WHEN the comments part does not parse
         *   CONSEQUENCE
         *     no comments rather than a broken read; the paragraph text is unaffected.
         */
        if (commentsDocument === null) return DocumentComments.none();
        const durableIdByParaId = commentsIdsDocument === null ? new Map<string, string>() : DocumentComments.durableIdsByParaId(commentsIdsDocument);
        const extensionByParaId = commentsExtendedDocument === null ? new Map<string, CommentExtension>() : DocumentComments.extensionsByParaId(commentsExtendedDocument);
        const commentsWithParagraphIds: CommentWithParagraphIds[] = [];
        for (const commentElement of Array.from(commentsDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "comment"))) {
            const referenceId = wordIdOf(commentElement);
            /**
             * WHEN a comment carries no `w:id`
             *   CONSEQUENCE
             *     nothing in the body can point at it; skip it.
             */
            if (referenceId === null) continue;
            commentsWithParagraphIds.push({
                referenceId,
                comment: {
                    id: DocumentComments.durableIdOf(commentElement, durableIdByParaId) ?? referenceId,
                    author: commentElement.getAttributeNS(WORDPROCESSINGML_NS, "author") ?? commentElement.getAttribute("w:author") ?? "",
                    text: DocumentComments.textOf(commentElement),
                    date: DocumentComments.dateOf(commentElement),
                    repliesTo: null,
                    resolved: DocumentComments.threadIsResolved(DocumentComments.lastParaIdOf(commentElement), extensionByParaId),
                },
                paragraphIds: DocumentComments.paragraphIdsOf(commentElement),
            });
        }
        DocumentComments.threadReplies(commentsWithParagraphIds, extensionByParaId);
        return new DocumentComments(new Map(commentsWithParagraphIds.map((entry) => [entry.referenceId, entry.comment])));
    }

    /**
     * Reads the comment parts out of a flat OPC `pkg:package`, the shape Word
     * hands the add-in for a range; a bare `w:document` or paragraph fragment
     * has no parts and yields no comments.
     */
    static fromPackage(packageXml: string): DocumentComments {
        const packageDocument = parseXmlDocumentOrNull(packageXml);
        /**
         * WHEN the markup does not parse as a whole document
         *   CONSEQUENCE
         *     it is a fragment; fragments carry no comment parts.
         */
        if (packageDocument === null) return DocumentComments.none();
        return DocumentComments.fromParts({
            commentsXml: flatOpcPartXml(packageDocument, COMMENTS_PART_NAME),
            commentsIdsXml: flatOpcPartXml(packageDocument, COMMENTS_IDS_PART_NAME),
            commentsExtendedXml: flatOpcPartXml(packageDocument, COMMENTS_EXTENDED_PART_NAME),
        });
    }

    /** The comments a paragraph references, in the order its `w:commentReference` runs appear; unknown ids are skipped. */
    forReferenceIds(referenceIds: readonly string[]): DocumentComment[] {
        const comments: DocumentComment[] = [];
        for (const referenceId of referenceIds) {
            const comment = this.commentByReferenceId.get(referenceId);
            /**
             * WHEN the paragraph points at a comment the parts do not hold
             *   CONSEQUENCE
             *     skip the marker; Word does the same when it renders.
             */
            if (comment !== undefined) comments.push(comment);
        }
        return comments;
    }

    /**
     * `commentsIds.xml` maps a comment paragraph's `paraId` to a hex durable
     * id. Ids out of Word's range are dropped so a malformed file falls back
     * to the reference id instead of inventing one.
     */
    private static durableIdsByParaId(commentsIdsDocument: Document): Map<string, string> {
        const durableIdByParaId = new Map<string, string>();
        for (const commentId of Array.from(commentsIdsDocument.getElementsByTagNameNS(WORDML_2016_CID_NS, "commentId"))) {
            const paraId = commentId.getAttributeNS(WORDML_2016_CID_NS, "paraId") ?? commentId.getAttribute("w16cid:paraId");
            const durableHex = commentId.getAttributeNS(WORDML_2016_CID_NS, "durableId") ?? commentId.getAttribute("w16cid:durableId");
            /**
             * WHEN the entry lacks either attribute or the hex is malformed
             *   CONSEQUENCE
             *     skip it.
             */
            if (paraId === null || durableHex === null || !DURABLE_ID_HEX.test(durableHex)) continue;
            const durableId = parseInt(durableHex, 16);
            /**
             * WHEN the id is zero or at or above the 31-bit ceiling
             *   CONSEQUENCE
             *     skip it; Word would not have written it.
             */
            if (durableId <= 0 || durableId >= DURABLE_ID_MAX_EXCLUSIVE) continue;
            durableIdByParaId.set(paraId.toUpperCase(), String(durableId));
        }
        return durableIdByParaId;
    }

    /**
     * `commentsExtended.xml` holds one `w15:commentEx` per comment, filed under
     * the `paraId` of the comment's last paragraph: `w15:done` says whether Word
     * marked it resolved, `w15:paraIdParent` names the comment a reply answers.
     *
     * EXAMPLE
     *   <w15:commentEx w15:paraId="1A2B3C4D" w15:done="1"/>                               -> 1A2B3C4D marked done
     *   <w15:commentEx w15:paraId="5E6F7A8B" w15:paraIdParent="1A2B3C4D" w15:done="0"/>   -> a reply to 1A2B3C4D
     */
    private static extensionsByParaId(commentsExtendedDocument: Document): Map<string, CommentExtension> {
        const extensionByParaId = new Map<string, CommentExtension>();
        for (const commentExtension of Array.from(commentsExtendedDocument.getElementsByTagNameNS(WORDML_2012_NS, "commentEx"))) {
            const paraId = commentExtension.getAttributeNS(WORDML_2012_NS, "paraId") ?? commentExtension.getAttribute("w15:paraId");
            /**
             * WHEN the entry names no paragraph
             *   CONSEQUENCE
             *     skip it: no comment can be matched to it.
             */
            if (paraId === null) continue;
            const repliedToParaId = commentExtension.getAttributeNS(WORDML_2012_NS, "paraIdParent") ?? commentExtension.getAttribute("w15:paraIdParent");
            const doneValue = commentExtension.getAttributeNS(WORDML_2012_NS, "done") ?? commentExtension.getAttribute("w15:done");
            extensionByParaId.set(paraId.toUpperCase(), {
                markedDone: doneValue !== null && OOXML_TRUE_VALUES.has(doneValue),
                repliedToParaId: repliedToParaId === null ? null : repliedToParaId.toUpperCase(),
            });
        }
        return extensionByParaId;
    }

    /**
     * The `paraId` of the comment's last paragraph, upper-cased: the key
     * `commentsExtended.xml` files the comment under.
     *
     * NULL
     *   The comment has no paragraph, or its last paragraph carries no `w14:paraId`
     *   (a file older Word wrote): it cannot be matched to an entry.
     *
     * EXAMPLE
     *   a comment of 3 paragraphs -> index 3 - 1 = 2, the last one
     */
    private static lastParaIdOf(commentElement: Element): string | null {
        const commentParagraphs = Array.from(commentElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"));
        const lastCommentParagraph = commentParagraphs[commentParagraphs.length - 1];
        if (lastCommentParagraph === undefined) return null;
        const paraId = lastCommentParagraph.getAttributeNS(WORDML_2010_NS, "paraId") ?? lastCommentParagraph.getAttribute("w14:paraId");
        return paraId === null ? null : paraId.toUpperCase();
    }

    /**
     * Whether the thread a comment belongs to is resolved.
     *
     * CASES
     *   - no paraId, or no entry for it                   -> open
     *   - its own entry is marked done                    -> resolved
     *   - it replies to a comment whose entry is marked
     *     done                                            -> resolved: resolving a thread resolves its replies
     *   - otherwise                                       -> open
     */
    private static threadIsResolved(lastParaId: string | null, extensionByParaId: ReadonlyMap<string, CommentExtension>): boolean {
        const extension = lastParaId === null ? undefined : extensionByParaId.get(lastParaId);
        if (extension === undefined) return false;
        if (extension.markedDone) return true;
        return extension.repliedToParaId !== null && extensionByParaId.get(extension.repliedToParaId)?.markedDone === true;
    }

    /** The first paragraph of the comment whose `paraId` the ids part knows names the durable id. */
    private static durableIdOf(commentElement: Element, durableIdByParaId: ReadonlyMap<string, string>): string | null {
        for (const paragraph of Array.from(commentElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"))) {
            const paraId = paragraph.getAttributeNS(WORDML_2010_NS, "paraId") ?? paragraph.getAttribute("w14:paraId");
            /**
             * CASES
             *   - the paragraph has a `w14:paraId` -> look it up, upper-cased: Word writes the hex in
             *                                         either case across the two parts
             *   - it has none                      -> nothing to look up; try the next paragraph
             */
            const durableId = paraId === null ? undefined : durableIdByParaId.get(paraId.toUpperCase());
            /**
             * WHEN this paragraph's paraId is in the ids part
             *   CONSEQUENCE
             *     that is the comment's durable id.
             */
            if (durableId !== undefined) return durableId;
        }
        return null;
    }

    /**
     * Sets `repliesTo` on every comment whose `w15:commentEx` entry names a
     * parent (`repliedToParaId`), to the parent comment's `id`. Any paragraph of
     * either comment may carry the joining `paraId`: Word keys an entry by the
     * last paragraph, and matching every paragraph also joins a file another
     * tool wrote.
     *
     * EXAMPLE
     *   <w15:commentEx w15:paraId="00000078" w15:paraIdParent="49B7864D"/>  -> 00000078 replies to 49B7864D
     *   <w15:commentEx w15:paraId="49B7864D"/>                              -> no parent: it starts a thread
     *
     * CASES
     *   - the comment's entry names a parent comment the parts hold -> repliesTo = that comment's id
     *   - it names a parent no comment carries                      -> repliesTo stays null
     *   - it names no parent, or an empty one                       -> repliesTo stays null
     */
    private static threadReplies(commentsWithParagraphIds: CommentWithParagraphIds[], extensionByParaId: ReadonlyMap<string, CommentExtension>): void {
        const commentIdByParagraphId = new Map<string, string>();
        for (const entry of commentsWithParagraphIds) {
            for (const paragraphId of entry.paragraphIds) commentIdByParagraphId.set(paragraphId, entry.comment.id);
        }
        for (const entry of commentsWithParagraphIds) {
            /**
             * CASES, for each of the comment's paragraphs in order
             *   - its entry names a non-empty parent `paraId` -> that `paraId`; the first such paragraph wins
             *   - it has no entry, or the entry names none    -> skipped
             */
            const parentParagraphId = entry.paragraphIds
                .map((paragraphId) => extensionByParaId.get(paragraphId)?.repliedToParaId)
                .find((repliedToParaId): repliedToParaId is string => typeof repliedToParaId === "string" && repliedToParaId !== "");
            const parentCommentId = parentParagraphId === undefined ? undefined : commentIdByParagraphId.get(parentParagraphId);
            /**
             * WHEN the parent resolves to a comment other than this one
             *   CONSEQUENCE
             *     record it; a self-reference from a malformed file stays a thread of its own.
             */
            if (parentCommentId !== undefined && parentCommentId !== entry.comment.id) entry.comment.repliesTo = parentCommentId;
        }
    }

    /** The upper-cased `w14:paraId` of each paragraph of the comment, in order; paragraphs without one are left out. */
    private static paragraphIdsOf(commentElement: Element): string[] {
        const paragraphIds: string[] = [];
        for (const paragraph of Array.from(commentElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"))) {
            const paraId = paragraph.getAttributeNS(WORDML_2010_NS, "paraId") ?? paragraph.getAttribute("w14:paraId");
            /**
             * CASES
             *   - the paragraph has a `w14:paraId` -> keep it, upper-cased, to join commentsExtended entries
             *   - it has none (older Word)         -> nothing can name it; leave it out
             */
            if (paraId !== null) paragraphIds.push(paraId.toUpperCase());
        }
        return paragraphIds;
    }

    /**
     * The comment's `w:date`.
     *
     * CASES
     *   - present and non-empty -> that string, as Word wrote it
     *   - absent or empty       -> null: Word drops dates when personal information is removed
     */
    private static dateOf(commentElement: Element): string | null {
        const date = commentElement.getAttributeNS(WORDPROCESSINGML_NS, "date") ?? commentElement.getAttribute("w:date");
        /**
         * CASES
         *   - `w:date` absent or empty -> null
         *   - otherwise                -> the date as written
         */
        return date === null || date === "" ? null : date;
    }

    /** The comment body as plain text: each paragraph's `w:t` runs joined, paragraphs joined by a newline, blank paragraphs dropped. */
    private static textOf(commentElement: Element): string {
        const paragraphTexts: string[] = [];
        for (const paragraph of Array.from(commentElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"))) {
            const text = Array.from(paragraph.getElementsByTagName("*"))
                .filter((element) => elementNameWithoutPrefix(element) === "t")
                .map((element) => element.textContent ?? "")
                .join("");
            /**
             * WHEN the paragraph holds no text (Word writes an empty trailing paragraph)
             *   CONSEQUENCE
             *     leave it out of the body.
             */
            if (text.length > 0) paragraphTexts.push(text);
        }
        return paragraphTexts.join("\n");
    }
}
